// apps/share-web/src/live/live-client.ts
//
// Лайв на телефоне читателя: опрос якоря + часовая модель, наружу —
// `PositionSource` из chart-follow.ts.
//
// Здесь только ПРОВОДКА. Вся арифметика живёт в `live-model.ts` и тестируется
// без сети и без таймеров; HTTP — в `live-api.ts`. Часы, планировщик и fetch
// приезжают инъекцией — тем же приёмом, что `Viewport` в chart-follow.ts. Там
// причина была измеренная (happy-dom не считает раскладку вовсе), здесь она той
// же природы: утверждение «следующий опрос назначен через 30 с» недоказуемо,
// если 30 с надо переждать.
//
// КАДРОМ МОДУЛЬ НЕ ВЛАДЕЕТ. Своего requestAnimationFrame здесь нет и заводить
// его нельзя: поверхность держит ОДИН кадр и тикает им и ведение листа, и
// шапку, и кнопку лайва. В волне A.1 у ChartFollow была своя пара
// start()/stop() с собственным rAF, её не звал никто, и она была удалена как
// ловушка: следующая поверхность позвала бы start() как выглядящий уместным API
// и получила бы двойной тик каждый кадр. Опрос по setTimeout — другое дело: он
// нужен, потому что сеть кадром не управляется, и он здесь.
//
// Своего start() у клиента тоже нет по тому же уроку: первый опрос назначается
// при создании. Клиент, который создали и забыли включить, — это ровно тот
// мёртвый API, из-за которого правило и появилось.

import type { PositionSource, SectionSec } from '../chart/chart-follow.js';
import { fetchLiveAnchor } from './live-api.js';
import {
  applyResult,
  derivePosition,
  emptyLiveModel,
  linkStateOf,
  msToNextBoundary,
  nextPollDelayMs,
  type LinkState,
  type LiveModel,
} from './live-model.js';

export interface LiveClientDeps {
  /** UUID текстовой ссылки — тот же, что в адресе `/t/<uuid>`. */
  textShareId: string;
  /**
   * Происхождение API. Лайв ходит по apiBase (WORKER_BASE), а не по origin
   * страницы: ссылка живёт на share.bandway.app, маршруты — на воркере.
   */
  apiBase?: string;
  fetchImpl?: typeof fetch;
  /** Локальные часы. Участвуют ТОЛЬКО в дельте между опросами (правило 1). */
  now?: () => number;
  schedule?: (fn: () => void | Promise<void>, ms: number) => unknown;
  cancel?: (handle: unknown) => void;
  /**
   * Видима ли вкладка СЕЙЧАС. Спрашивается в момент назначения срока и НЕ
   * запоминается — в этом весь приём: каденцию вне лайва меняет тот же
   * единственный таймер, а не второй, заведённый на смене видимости. Своего
   * слушателя `visibilitychange` здесь поэтому нет вовсе; страница и так зовёт
   * `poll()` на возврате из фона (words-view.ts), а `poll()` снимает прежний
   * срок первой строкой — то есть возврат из фона переводит каденцию на видимую
   * ровно одним таймером. Осиротевший срок рядом с новым — уже случавшийся
   * класс ошибки, и цена ему удвоенная частота запросов со всего зала.
   */
  visible?: () => boolean;
  /**
   * Расписание песни — для внепланового опроса к границе секции. Не задано —
   * границ нет, каденция ровная. Модуль расписания не выдумывает.
   */
  sectionsOf?: (songIdx: number) => readonly SectionSec[];
  /**
   * Длительность песни в секундах, или null — неизвестна. Правило 5 без неё не
   * применяется: соврать «песня кончилась» не лучше, чем экстраполировать.
   */
  durationSecOf?: (songIdx: number) => number | null;
  /**
   * Привести содержимое страницы к ревизии `rev` (спека §4.4).
   *
   * Зовётся ПЕРЕД тем, как якорь попадёт в модель, и вернуть обязан `true`
   * только если страница теперь держит именно эту ревизию (или уже держала).
   * `false` означает «список у меня прежний» — и тогда клиент якорь НЕ
   * применяет: индекс, посчитанный по новому списку, приложенный к старому, это
   * и есть «лидер объявил одну песню, зал открыл соседнюю».
   *
   * Не задан — поведение ровно как до ревизий.
   */
  syncContent?: (rev: number) => boolean | Promise<boolean>;
}

export interface LiveClient {
  /** Для `createChartFollow` — тот самый шов, заведённый в волне A. */
  position: PositionSource;
  /**
   * Состояние связи СЕЙЧАС. Считается по требованию, а не рассылается
   * подписчикам: `stale` наступает от одного лишь хода времени, без единого
   * события, — подписка на него была бы либо ложью, либо вторым кадром.
   * Поверхность читает это в своём кадре, как и позицию.
   */
  state(): LinkState;
  /**
   * Опросить немедленно, вне очереди. Страница зовёт это на возврате из фона
   * (`visibilitychange`): вкладку могли держать свёрнутой полчаса, и ждать
   * очередного срока значило бы показать музыканту протухший якорь на первом
   * же взгляде.
   */
  poll(): Promise<void>;
  /** Снять запланированный опрос. После этого клиент молчит навсегда. */
  stop(): void;
}

export function createLiveClient(deps: LiveClientDeps): LiveClient {
  const {
    textShareId,
    apiBase = '',
    fetchImpl = fetch,
    now = () => Date.now(),
    schedule = (fn, ms) => setTimeout(() => { void fn(); }, ms),
    cancel = (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
    // Нет document (воркер, node-тест) — считаем, что смотрят: страница без
    // понятия о видимости это открытая страница, и медленная половина выбора ей
    // досталась бы молча.
    visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
    sectionsOf,
    durationSecOf,
    syncContent,
  } = deps;

  let model: LiveModel = emptyLiveModel();
  let timer: unknown = null;
  let stopped = false;
  /**
   * Идёт ли опрос ПРЯМО СЕЙЧАС (находка сквозного ревью 17.09).
   *
   * `clearTimer()` в начале `poll` снимает НАЗНАЧЕННЫЙ срок, но против опроса,
   * который ещё в полёте, он бессилен: `timer` у того уже null, и его ответ
   * поставит второй срок поверх назначенного. Дальше каждый возврат из фона
   * удваивал бы частоту опроса всего зала — шапка модуля называет это уже
   * случавшимся классом ошибки. Эта волна удлинила окно вдвое (внутрь poll
   * добавился второй сетевой круг — перечитывание нагрузки) и добавила цену:
   * два наложенных опроса дают два перемонтажа листа у каждого читателя.
   */
  let inFlight = false;

  const position: PositionSource = {
    now: () => derivePosition(model, now(), durationSecOf),
  };

  const state = (): LinkState => linkStateOf(model, now());

  /** Срок снимается только когда он ещё не сработал: handle обнуляет сам вызов. */
  const clearTimer = (): void => {
    if (timer !== null) { cancel(timer); timer = null; }
  };

  const arm = (ms: number): void => {
    timer = schedule(() => { timer = null; return poll(); }, ms);
  };

  const scheduleNext = (): void => {
    // Мёртвую ссылку опрашивать нечего: 410 отдал СЕРВЕР ССЫЛКИ, то есть
    // маршрут жив и ответ окончательный. 404 сюда сознательно не попадает —
    // его получает и телефон, открывший ссылку до выката воркера, и замолчать
    // навсегда значило бы не ожить после деплоя без перезагрузки страницы.
    if (stopped || model.terminal === 'ended') return;

    // Каденцию решает СОСТОЯНИЕ СВЯЗИ, а не наличие выведенной позиции, и это
    // не одно и то же в обе стороны: за концом песни (правило 5) позиции нет, но
    // якорь свежий и лидер наверняка уже перешёл на следующую — опрашивать там
    // раз в 30 с значило бы догонять его дольше всего ровно тогда, когда он
    // точно двинулся. А при протухшем якоре позиция ЕСТЬ (лист ведём по слою 2),
    // но связи нет, и частить незачем.
    const st = state();
    // Внеплановый опрос к границе секции — роскошь ЖИВОЙ сессии: он ловит
    // неожиданный скачок лидера. Пока якорь протух, ловить нечего — сервер и
    // так недостижим, а частота опроса это единственный настоящий рычаг цены.
    const at = st === 'live' ? position.now() : null;
    const boundary = at && sectionsOf
      ? msToNextBoundary(sectionsOf(at.songIdx), at.posSec)
      : null;
    // Видимость спрашивается ЗДЕСЬ, в момент назначения срока, — этим и меняется
    // каденция на смене видимости, без второго таймера рядом с первым.
    arm(nextPollDelayMs({ state: st, boundaryInMs: boundary, visible: visible() }));
  };

  async function poll(): Promise<void> {
    // Внеплановый опрос ОТМЕНЯЕТ очередной срок. Без этой строки возврат из
    // фона оставлял бы прежний таймер жить рядом с новым, и дальше каждый
    // возврат удваивал бы частоту опроса всего зала — тот же класс, что двойной
    // тик кадра в волне A.1, только оплачиваемый по счётчику запросов.
    clearTimer();
    // Летящий опрос уже принесёт свежий якорь и сам назначит следующий срок:
    // второй круг рядом с ним — это лишний запрос от каждого телефона в зале и
    // лишний перемонтаж листа, а не более свежие данные.
    if (inFlight) return;
    inFlight = true;
    try {
    // Часы едут ТЕМ ЖЕ швом: опрос измеряет длину ответа, чтобы снять постоянную
    // половину отставания (live-api.ts, `halfRttMs`). Вторых часов у клиента нет.
    const res = await fetchLiveAnchor(textShareId, apiBase, fetchImpl, now);
    // Ответ мог вернуться уже после ухода страницы. Принять его безвредно,
    // назначить по нему следующий срок — нет: именно так цикл опроса и
    // переживает поверхность, которая его завела.
    if (stopped) return;

    // ПОРЯДОК — И ЕСТЬ ГАРАНТИЯ (спека §4.4). Индекс применяется только к тому
    // списку, для которого посчитан, поэтому перечитывание идёт ДО applyResult,
    // а не после и не параллельно. Обратный порядок — это тот же отказ, только
    // растянутый на время загрузки.
    const rev = (res as { contentRev?: number }).contentRev;
    if (typeof rev === 'number' && syncContent) {
      let ready = false;
      // Бросок приравнен к «не смог»: сеть у зала отваливается, и уронить на
      // этом цикл опроса значило бы обменять устаревшие слова на молчание
      // навсегда.
      try { ready = await syncContent(rev); } catch { ready = false; }
      if (stopped) return;
      if (!ready) { scheduleNext(); return; }
    }

    // ⚠️ ЯКОРЬ ИЗ ЧУЖОЙ РЕВИЗИИ НЕ ПРИМЕНЯЕТСЯ (блокер 2 сквозного ревью 17.09).
    //
    // До этой проверки §4.4 держалась на дисциплине десктопа: телефон приводил
    // СЛОВА к ревизии содержимого, а индекс брал из якоря, который мог быть
    // записан под другой. Отказ воспроизводим без всякого шага 4: лидер на
    // песне A, сетлист переиздали без A, десктоп пересчитать индекс не может и
    // якорь не переписывает — телефон честно перечитывает слова и применяет к
    // ним старый индекс, открывая чужую песню на все 25 секунд до протухания.
    //
    // Гарантия переехала с обещания на контракт: сравниваются ДВА числа из
    // одного ответа, и телефону больше не нужно верить, что лидер успел
    // поправиться.
    const anchorRev = res.state === 'anchor' ? res.anchor.contentRev : undefined;
    if (res.state === 'anchor' && typeof anchorRev === 'number' && typeof rev === 'number'
        && anchorRev !== rev) {
      scheduleNext();
      return;
    }

    model = applyResult(model, res, now());
    scheduleNext();
    } finally { inFlight = false; }
  }

  const stop = (): void => {
    stopped = true;
    clearTimer();
  };

  // Первый опрос — сразу: срок 0, а не вызов на месте, чтобы создание клиента
  // оставалось синхронным и отменяемым (stop() до первого ответа обязан снять
  // и его тоже).
  arm(0);

  return { position, state, poll, stop };
}
